<template>
  <div>
    <b-card>
      <b-row>
        <div class="col-12 mt-16">
          <b-form @submit.prevent="updateProduct" v-if="show">
            <!-- Header -->
            <div
              style="background-color: rgb(97, 116, 152); height: 32px; border-radius: 4px;"
            >
              <h4 style="color: rgb(223, 227, 238); margin-left: 5px; font-weight: bold;">
                Edit Product
              </h4>
            </div>

            <div class="row">
              <!-- Title -->
              <div class="col-md-4 col-12">
                <b-form-group label="Title:" label-for="name">
                  <b-form-input id="name" placeholder="Enter name" :state="validateField(name, 'text')" v-model="name" required />
                </b-form-group>
              </div>

              <!-- Price -->
              <div class="col-md-4 col-12">
                <b-form-group label="Amount:" label-for="price">
                  <b-form-input id="price" placeholder="Enter price" :state="validateField(price)" v-model="price" />
                </b-form-group>
              </div>

              <!-- Quantity -->
              <div class="col-md-4 col-12">
                <b-form-group label="Quantity:" label-for="quantity">
                  <b-form-input id="quantity" placeholder="Enter quantity" :state="validateField(quantity)" v-model="quantity" />
                </b-form-group>
              </div>
              <div class="col-md-4 col-12">
                <b-form-group label="Sequence / Position:" label-for="position">
                  <b-form-input
                    id="position"
                    type="number"
                    min="0"
                    placeholder="Enter sequence/position"
                    :state="validateField(position)"
                    v-model="position"
                  />
                </b-form-group>
              </div>

              <!-- Description -->
              <div class="col-md-12 col-12">
                <b-form-group label="Description:" label-for="description">
                  <textarea
                    class="form-control"
                    id="description"
                    placeholder="Enter description"
                    :class="{  'is-invalid': validateField(description) === false,  'is-valid': validateField(description) === true,}"
                    v-model="description"
                  />
                </b-form-group>
              </div>

              <!-- Image Upload Section -->
              <div class="col-12">
                <b-form-group label="Product Images:" label-class="font-weight-bold">
                  <!-- Drag & Drop Upload -->
                  <div
                    class="drag-drop-area"
                    :class="{ 'drag-over': isDragOver }"
                    @dragover.prevent="onDragOver"
                    @dragleave.prevent="onDragLeave"
                    @drop.prevent="onDrop"
                    @click="triggerFileInput"
                  >
                    <div class="upload-content">
                      <i class="fas fa-cloud-upload-alt fa-2x text-muted"></i>
                      <h5 class="upload-title">Drag & Drop or Click to Upload</h5>
                      <p class="upload-subtitle">Max 20 images, 2MB each</p>
                    </div>
                    <input
                      type="file"
                      ref="fileInput"
                      accept="image/*"
                      multiple
                      @change="onFileSelect"
                      style="display: none;"
                    />
                  </div>

                  <!-- Existing Images -->
                  <div v-if="imageArray.length" class="mt-4">
                    <h6 class="mb-2">Current Images:</h6>
                    <div class="image-grid">
                      <div
                        v-for="(img, index) in imageArray"
                        :key="'existing-' + index"
                        class="image-card"
                      >
                        <div class="image-container">
                          <img
                            :src="'https://wossdb.wossautoid.com/' + img"
                            class="image-preview"
                          />
                          <div class="image-overlay">
                            <button
                              type="button"
                              class="remove-btn"
                              @click="removeExistingImage(index)"
                            >
                              ✕
                            </button>
                            <button
                              type="button"
                              class="view-btn"
                              @click="viewImage('https://wossdb.wossautoid.com/' + img)"
                            >
                              👁
                            </button>
                          </div>
                        </div>
                        <div class="image-info">
                          <span class="image-status existing">Existing</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- New Previews -->
                  <div v-if="newImagePreviews.length" class="mt-4">
                    <h6 class="mb-2">New Images:</h6>
                    <div class="image-grid">
                      <div
                        v-for="(img, index) in newImagePreviews"
                        :key="'new-' + index"
                        class="image-card"
                      >
                        <div class="image-container">
                          <img :src="img.preview" class="image-preview" />
                          <div class="image-overlay">
                            <button
                              type="button"
                              class="remove-btn"
                              @click="removeNewImage(index)"
                            >
                              ✕
                            </button>
                            <button
                              type="button"
                              class="view-btn"
                              @click="viewImage(img.preview)"
                            >
                              👁
                            </button>
                          </div>
                        </div>
                        <div class="image-info">
                          <span class="image-name">{{ img.name }}</span>
                          <span class="image-size">{{ formatFileSize(img.size) }}</span>
                          <span class="image-status new">New</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </b-form-group>
              </div>
            </div>

            <!-- Submit -->
            <b-button type="submit" variant="primary" class="mt-3" :disabled="isLoading">
              <span v-if="!isLoading">Update</span>
              <b-spinner v-else small />
            </b-button>
          </b-form>
        </div>
      </b-row>
    </b-card>

    <!-- Image Modal -->
    <b-modal v-model="showImageModal" size="lg" hide-footer centered>
      <template #modal-title>Image Preview</template>
      <div class="text-center">
        <img :src="modalImageSrc" class="img-fluid" style="max-height: 70vh;" />
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BSpinner,
  BModal,
} from "bootstrap-vue";
import axios from "../../../axios";

export default {
  data() {
    return {
      show: true,
      isLoading: false,
      name: "",
      price: "",
      description: "",
      quantity: "",
      position: "",
      imageArray: [], // existing images
      newImages: [],
      newImagePreviews: [],
      removedExistingImages: [],
      isDragOver: false,
      showImageModal: false,
      modalImageSrc: "",
    };
  },
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BSpinner,
    BModal,
  },
  created() {
    const productId = this.$route.params.id;
    axios.get(`ProductShow/${productId}`).then((res) => {
      const product = res.data.data;
      this.name = product.name;
      this.price = product.price;
      this.description = product.description;
      this.quantity = product.quantity;
      this.position = product.position || product.sequence || "";
      this.imageArray = product.image || [];
      this.editedUser = product;
    });
  },
  methods: {
    validateField(value, type = "text") {
            if (value === null || value === undefined || value === "") return null;

            if (type === "email") {
            const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return regex.test(value);
            }
            if (type === "number") {
            const regex = /^\+?[0-9 ]+$/; // accepts + and spaces
            return regex.test(value.toString().trim());
            }
            return value.toString().trim().length > 0;
      },
    async updateProduct() {
      this.isLoading = true;
      const formData = new FormData();
      formData.append("name", this.name);
      formData.append("price", this.price);
      formData.append("description", this.description);
      formData.append("quantity", this.quantity);
      formData.append("position", this.position);
      formData.append("sequence", this.position);

      this.newImages.forEach((file, i) => {
        formData.append(`image[${i}]`, file);
      });

      if (this.removedExistingImages.length > 0) {
        formData.append("removed_images", JSON.stringify(this.removedExistingImages));
      }

      await axios.post(`ProductUpdate/${this.editedUser.id}`, formData)
      .then(() => {
          this.$bvToast.toast("Product updated successfully!", {
            title: "Success",
            variant: "primary",
            solid: true,
            toaster: "b-toaster-top-right",
            autoHideDelay: 5000,
          });
          this.isLoading = false;
          this.$router.push({ name: "ListProduct" });
        })
        .catch((error) => {
          console.error("Update error:", error);
          this.isLoading = false;
        });
    },

    // Drag & Drop
    onDragOver() {
      this.isDragOver = true;
    },
    onDragLeave() {
      this.isDragOver = false;
    },
    onDrop(e) {
      this.isDragOver = false;
      this.processFiles(Array.from(e.dataTransfer.files));
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    onFileSelect(e) {
      this.processFiles(Array.from(e.target.files));
      e.target.value = "";
    },

    // File Processing
    processFiles(files) {
      if (this.imageArray.length + this.newImages.length + files.length > 20) {
        this.$bvToast.toast("Maximum 20 images allowed.", {
          variant: "warning",
          solid: true,
        });
        return;
      }
      files.forEach((file) => {
        if (file.size > 2 * 1024 * 1024) {
          this.$bvToast.toast(`${file.name} exceeds 2MB limit.`, {
            variant: "danger",
            solid: true,
          });
          return;
        }
        this.newImages.push(file);
        this.newImagePreviews.push({
          preview: URL.createObjectURL(file),
          name: file.name,
          size: file.size,
        });
      });
    },

    removeNewImage(index) {
      this.newImages.splice(index, 1);
      this.newImagePreviews.splice(index, 1);
    },
    removeExistingImage(index) {
      this.removedExistingImages.push(this.imageArray[index]);
      this.imageArray.splice(index, 1);
    },
    viewImage(src) {
      this.modalImageSrc = src;
      this.showImageModal = true;
    },
    formatFileSize(bytes) {
      const sizes = ["Bytes", "KB", "MB"];
      if (bytes === 0) return "0 Bytes";
      const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10);
      return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
    },
  },
};
</script>

<style scoped>
/* Same style as Add Product */
.drag-drop-area {
  border: 2px dashed #cbd5e0;
  border-radius: 12px;
  background: #f8fafc;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 160px;
}
.drag-drop-area.drag-over {
  border-color: #4299e1;
  background: #ebf8ff;
}
.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
}
.image-card {
  border-radius: 12px;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
}
.image-container {
  aspect-ratio: 1;
  overflow: hidden;
}
.image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.image-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.6);
  opacity: 0;
  transition: 0.3s;
}
.image-card:hover .image-overlay {
  opacity: 1;
}
.remove-btn,
.view-btn {
  background: #fff;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.remove-btn {
  color: #e53e3e;
}
.view-btn {
  color: #3182ce;
}
.image-info {
  padding: 8px;
  font-size: 13px;
}
.image-status {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
}
.image-status.existing {
  background: #c6f6d5;
  color: #276749;
}
.image-status.new {
  background: #bee3f8;
  color: #2c5282;
}
</style>
